import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Loader2, Upload, X } from 'lucide-react';
import { Amenity, AmenityFormData } from '../../types/amenities/amenities';

interface AmenityFormProps {
  amenity?: Amenity;
  onSubmit: (data: Partial<AmenityFormData>) => void;
  onCancel: () => void;
  loading: boolean;
}

const AmenityForm: React.FC<AmenityFormProps> = ({ amenity, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<Partial<AmenityFormData>>({
    name: '',
    image: undefined,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (amenity) {
      setFormData({
        name: amenity.name,
        image: undefined,
      });
      setImagePreview(amenity.image || null);
    }
  }, [amenity]);

  const handleInputChange = (field: keyof AmenityFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: undefined,
    }));
    setImagePreview(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Only send name and image
    const submitData: Partial<AmenityFormData> = {
      name: formData.name,
      image: formData.image,
    };
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div className='space-y-2'>
        <Label htmlFor='name'>Name *</Label>
        <Input
          id='name'
          value={formData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder='Enter amenity name'
          required
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='image'>Image *</Label>
        <div className='flex items-center space-x-4'>
          <div className='relative'>
            <Input
              id='image'
              type='file'
              accept='image/*'
              onChange={handleImageChange}
              className='hidden'
              required={!amenity}
            />
            <Button
              type='button'
              variant='outline'
              onClick={() => document.getElementById('image')?.click()}
              className='flex items-center space-x-2'
            >
              <Upload className='h-4 w-4' />
              <span>Upload Image</span>
            </Button>
          </div>
          {imagePreview && (
            <div className='relative'>
              <Avatar className='h-12 w-12'>
                <AvatarImage src={imagePreview} alt='Preview' />
                <AvatarFallback>IMG</AvatarFallback>
              </Avatar>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={removeImage}
                className='absolute -top-2 -right-2 h-6 w-6 p-0'
              >
                <X className='h-3 w-3' />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className='flex justify-end space-x-2 pt-4'>
        <Button type='button' variant='outline' onClick={onCancel}>
          Cancel
        </Button>
        <Button type='submit' disabled={loading}>
          {loading ? (
            <>
              <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              {amenity ? 'Updating...' : 'Creating...'}
            </>
          ) : amenity ? (
            'Update Amenity'
          ) : (
            'Create Amenity'
          )}
        </Button>
      </div>
    </form>
  );
};

export default AmenityForm;
